import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["toggle", "dropdown", "buttonText", "chevron", "checkbox", "option", "searchInput", "emptyState"]
  static values = { 
    fieldName: String,
    currentValues: String 
  }

  connect() {
    this.selectedValues = new Set()
    this.allOptions = []
    
    // Initialize selected values from current state
    if (this.currentValuesValue) {
      this.currentValuesValue.split(',').forEach(value => {
        if (value.trim()) {
          this.selectedValues.add(value.trim())
        }
      })
    }
    
    // Store all available options for filtering
    this.optionTargets.forEach(option => {
      this.allOptions.push({
        element: option,
        value: option.dataset.optionValue,
        text: option.dataset.optionText.toLowerCase()
      })
    })
    
    // Set initial checkbox states
    this.updateCheckboxStates()
    this.updateButtonText()
    
    // Close dropdown when clicking outside
    this.boundCloseDropdownOnOutsideClick = this.closeDropdownOnOutsideClick.bind(this)
    document.addEventListener('click', this.boundCloseDropdownOnOutsideClick)
    
    // Handle escape key
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    document.addEventListener('keydown', this.boundHandleKeydown)
  }

  disconnect() {
    document.removeEventListener('click', this.boundCloseDropdownOnOutsideClick)
    document.removeEventListener('keydown', this.boundHandleKeydown)
  }

  toggleDropdown(event) {
    event.stopPropagation()
    
    if (this.dropdownTarget.classList.contains('hidden')) {
      this.openDropdown()
    } else {
      this.closeDropdown()
    }
  }

  openDropdown() {
    this.dropdownTarget.classList.remove('hidden')
    this.toggleTarget.setAttribute('aria-expanded', 'true')
    this.chevronTarget.style.transform = 'rotate(180deg)'
    
    // Focus search input if present
    if (this.hasSearchInputTarget) {
      setTimeout(() => this.searchInputTarget.focus(), 100)
    }
    
    // Announce to screen readers
    this.announceToScreenReader('Dropdown opened')
  }

  closeDropdown() {
    this.dropdownTarget.classList.add('hidden')
    this.toggleTarget.setAttribute('aria-expanded', 'false')
    this.chevronTarget.style.transform = 'rotate(0deg)'
    
    // Clear search when closing
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = ''
      this.showAllOptions()
    }
  }

  toggleOption(event) {
    event.stopPropagation()
    
    const optionElement = event.currentTarget
    const value = optionElement.dataset.optionValue
    const checkbox = optionElement.querySelector('input[type="checkbox"]')
    
    if (this.selectedValues.has(value)) {
      this.selectedValues.delete(value)
      checkbox.checked = false
      optionElement.setAttribute('aria-selected', 'false')
    } else {
      this.selectedValues.add(value)
      checkbox.checked = true
      optionElement.setAttribute('aria-selected', 'true')
    }
    
    this.updateButtonText()
    this.updateSelectedIndicators()
    
    // Trigger change event for manual filter tracking
    checkbox.dispatchEvent(new Event('change', { bubbles: true }))
    
    // Announce change to screen readers
    const optionText = optionElement.dataset.optionText
    const action = checkbox.checked ? 'selected' : 'deselected'
    this.announceToScreenReader(`${optionText} ${action}`)
  }

  selectAll(event) {
    event.stopPropagation()
    
    // Get all visible options (respecting search filter)
    const visibleOptions = this.optionTargets.filter(option => 
      !option.classList.contains('hidden')
    )
    
    visibleOptions.forEach(option => {
      const value = option.dataset.optionValue
      const checkbox = option.querySelector('input[type="checkbox"]')
      
      this.selectedValues.add(value)
      checkbox.checked = true
      option.setAttribute('aria-selected', 'true')
    })
    
    this.updateButtonText()
    this.updateSelectedIndicators()
    
    // Trigger change events
    this.checkboxTargets.forEach(checkbox => {
      if (checkbox.checked) {
        checkbox.dispatchEvent(new Event('change', { bubbles: true }))
      }
    })
    
    this.announceToScreenReader(`Selected all visible options`)
  }

  clearAll(event) {
    event.stopPropagation()
    
    this.selectedValues.clear()
    
    this.checkboxTargets.forEach(checkbox => {
      checkbox.checked = false
    })
    
    this.optionTargets.forEach(option => {
      option.setAttribute('aria-selected', 'false')
    })
    
    this.updateButtonText()
    this.updateSelectedIndicators()
    
    // Trigger change events
    this.checkboxTargets.forEach(checkbox => {
      checkbox.dispatchEvent(new Event('change', { bubbles: true }))
    })
    
    this.announceToScreenReader('Cleared all selections')
  }

  filterOptions(event) {
    const searchTerm = event.target.value.toLowerCase().trim()
    let visibleCount = 0
    
    this.allOptions.forEach(({ element, text }) => {
      if (!searchTerm || text.includes(searchTerm)) {
        element.classList.remove('hidden')
        visibleCount++
      } else {
        element.classList.add('hidden')
      }
    })
    
    // Show/hide empty state
    if (visibleCount === 0 && searchTerm) {
      this.emptyStateTarget.classList.remove('hidden')
    } else {
      this.emptyStateTarget.classList.add('hidden')
    }
    
    // Announce search results to screen readers
    if (searchTerm) {
      const message = visibleCount === 0 
        ? 'No options found' 
        : `${visibleCount} option${visibleCount === 1 ? '' : 's'} found`
      this.announceToScreenReader(message)
    }
  }

  showAllOptions() {
    this.optionTargets.forEach(option => {
      option.classList.remove('hidden')
    })
    this.emptyStateTarget.classList.add('hidden')
  }

  updateButtonText() {
    const selectedCount = this.selectedValues.size
    const fieldLabel = this.element.querySelector('label').textContent.trim().replace(/\d+\s+selected/, '').trim()
    
    let text
    if (selectedCount === 0) {
      text = `Select ${fieldLabel.toLowerCase()}...`
    } else if (selectedCount === 1) {
      const selectedValue = Array.from(this.selectedValues)[0]
      const option = this.optionTargets.find(opt => opt.dataset.optionValue === selectedValue)
      text = option ? option.dataset.optionText : selectedValue
    } else {
      const singularLabel = fieldLabel.replace(/s$/, '') // Simple pluralization
      text = `${selectedCount} ${singularLabel.toLowerCase()}${selectedCount === 1 ? '' : 's'} selected`
    }
    
    this.buttonTextTarget.textContent = text
    
    // Update the label count indicator
    const label = this.element.querySelector('label')
    const existingBadge = label.querySelector('.bg-blue-100')
    
    if (selectedCount > 0) {
      if (existingBadge) {
        existingBadge.textContent = `${selectedCount} selected`
      } else {
        const badge = document.createElement('span')
        badge.className = 'ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800'
        badge.textContent = `${selectedCount} selected`
        label.appendChild(badge)
      }
    } else if (existingBadge) {
      existingBadge.remove()
    }
  }

  updateSelectedIndicators() {
    this.optionTargets.forEach(option => {
      const value = option.dataset.optionValue
      const indicator = option.querySelector('svg')
      
      if (this.selectedValues.has(value)) {
        indicator?.classList.remove('hidden')
      } else {
        indicator?.classList.add('hidden')
      }
    })
  }

  updateCheckboxStates() {
    this.checkboxTargets.forEach(checkbox => {
      const value = checkbox.value
      checkbox.checked = this.selectedValues.has(value)
      
      // Update parent option aria-selected
      const option = checkbox.closest('[data-multi-select-dropdown-target="option"]')
      if (option) {
        option.setAttribute('aria-selected', checkbox.checked)
      }
    })
  }

  handleKeydown(event) {
    // Close dropdown on Escape
    if (event.key === 'Escape' && !this.dropdownTarget.classList.contains('hidden')) {
      event.preventDefault()
      this.closeDropdown()
      this.toggleTarget.focus()
    }
    
    // Handle arrow navigation when dropdown is open
    if (!this.dropdownTarget.classList.contains('hidden')) {
      const visibleOptions = this.optionTargets.filter(option => 
        !option.classList.contains('hidden')
      )
      
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault()
        this.navigateOptions(visibleOptions, event.key === 'ArrowDown')
      }
      
      // Enter key toggles focused option
      if (event.key === 'Enter') {
        const focusedOption = document.activeElement.closest('[data-multi-select-dropdown-target="option"]')
        if (focusedOption) {
          event.preventDefault()
          this.toggleOption({ currentTarget: focusedOption, stopPropagation: () => {} })
        }
      }
    }
  }

  navigateOptions(visibleOptions, isDown) {
    const currentFocus = document.activeElement.closest('[data-multi-select-dropdown-target="option"]')
    let nextIndex = 0
    
    if (currentFocus) {
      const currentIndex = visibleOptions.indexOf(currentFocus)
      if (isDown) {
        nextIndex = (currentIndex + 1) % visibleOptions.length
      } else {
        nextIndex = currentIndex > 0 ? currentIndex - 1 : visibleOptions.length - 1
      }
    }
    
    if (visibleOptions[nextIndex]) {
      visibleOptions[nextIndex].focus()
    }
  }

  announceToScreenReader(message) {
    // Create or update live region for announcements
    let liveRegion = document.getElementById('multi-select-announcements')
    if (!liveRegion) {
      liveRegion = document.createElement('div')
      liveRegion.id = 'multi-select-announcements'
      liveRegion.className = 'sr-only'
      liveRegion.setAttribute('aria-live', 'polite')
      liveRegion.setAttribute('aria-atomic', 'true')
      document.body.appendChild(liveRegion)
    }
    
    liveRegion.textContent = message
    
    // Clear message after announcement
    setTimeout(() => {
      if (liveRegion.textContent === message) {
        liveRegion.textContent = ''
      }
    }, 1000)
  }

  // Handle clicks outside dropdown
  closeDropdownOnOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this.closeDropdown()
    }
  }
}